import { createEffect, createEvent, createStore } from "effector"

import ArticleService from "services/ArticleService"
import ProductHierarchyProductLine from "models/ProductHierarchyProductLine"
import { useUnit } from "effector-react"
import { useCallback, useMemo } from "react"
import { ISelectOption } from "components/Select/SelectSearch"
import { Guid } from "@/types"
import ProductLine from "models/ProductLine"
import ProductFamily from "models/ProductFamily"
import ProductModel from "models/ProductModel"
import { orderBy } from "lodash"

export interface IFetchProductHierarchyParams {
    isExtManufacturer?: boolean
    onlyWithArticles?: boolean
    ignoreCache?: boolean
    onFail?: () => void
}

const $productHierarchy = createStore<ProductHierarchyProductLine[] | null>(null)
const $isLoadingProductHierarchy = createStore(false)
const setIsLoadingProductHierarchy = createEvent<boolean>()

const fetchProductHierarchy = createEffect(async ({
    isExtManufacturer,
    onlyWithArticles,
    ignoreCache,
    onFail,
} : IFetchProductHierarchyParams = {}) => {
    if ($isLoadingProductHierarchy.getState() && !ignoreCache) return

    setIsLoadingProductHierarchy(true)

    const currentProductHierarchy = $productHierarchy.getState()

    if (currentProductHierarchy && !ignoreCache) {
        return currentProductHierarchy
    }

    try {
        const fetchedProductHierarchy = await ArticleService.getProductHierarchy({ isExtManufacturer })
        if (onlyWithArticles) {
            return fetchedProductHierarchy.filter(line => line.hasArticles)
                .map(line => {
                    line.families = line.families.filter(family => family.hasArticles)
                        .map(family => {
                            family.models = family.models.filter(model => model.hasArticles)
                            return family
                        })
                    return line
                })
        }
        return fetchedProductHierarchy
    } catch(e) {
        if (typeof onFail === "function") {
            onFail()
        }
        return null
    }
})

$isLoadingProductHierarchy.on(setIsLoadingProductHierarchy, (_, value) => value)
$isLoadingProductHierarchy.on(fetchProductHierarchy.done, () => false)
$productHierarchy.on(fetchProductHierarchy.doneData, (_, productHierarchy) => productHierarchy)

export default function useProductHierarchy() {
    const productHierarchy = useUnit($productHierarchy)
    const isLoadingProductHierarchy = useUnit($isLoadingProductHierarchy)

    const productLineSelectOptions = useMemo<ISelectOption<Guid, ProductLine>[]>(() => {
        if (!productHierarchy || productHierarchy.length === 0) return []
        return orderBy(
            productHierarchy
                .map(item => ({
                    label: item.description,
                    value: item.id,
                    data: new ProductLine(item.id, item.description),
                })),
            "label"
        )
    }, [productHierarchy])

    const getProductFamilySelectOptions = useCallback((productLineId?: Guid | null): ISelectOption<Guid, ProductFamily>[] => {
        if (!productHierarchy || productHierarchy.length === 0) return []
        if (productLineId) {
            return orderBy(
                productHierarchy.find(item => item.id === productLineId)?.families.map(item => ({
                    label: item.description,
                    value: item.id,
                    data: new ProductFamily(item.id, item.description),
                })) || [],
                "label"
            )
        }
        return orderBy(
            productHierarchy.flatMap(item => item.families.map(family => ({
                label: family.description,
                value: family.id,
                data: new ProductFamily(item.id, item.description),
            }))),
            "label"
        )
    }, [productHierarchy])

    const getProductModelSelectOptions = useCallback((productLineId?: Guid | null, productFamilyId?: Guid | null): ISelectOption<Guid, ProductModel>[] => {
        if (!productHierarchy || productHierarchy.length === 0) return []
        if (!productLineId || !productFamilyId) {
            return orderBy(
                productHierarchy.flatMap(item => item.families.flatMap(family => family.models.map(model => ({
                    label: model.description,
                    value: model.id,
                    data: new ProductModel(model.id, model.description),
                })))),
                "label"
            )
        }
        const productLine = productHierarchy.find(item => item.id === productLineId)
        if (!productLine) return []
        const productFamily = productLine.families.find(item => item.id === productFamilyId)
        if (!productFamily) return []
        return orderBy(
            productFamily.models.map(item => ({
                label: item.description,
                value: item.id,
                data: new ProductModel(item.id, item.description),
            })),
            "label"
        )
    }, [productHierarchy])

    return {
        productHierarchy: isLoadingProductHierarchy ? null : productHierarchy,
        fetchProductHierarchy,
        productLineSelectOptions,
        getProductFamilySelectOptions,
        getProductModelSelectOptions,
    }
}
